/**
 * One place that talks to the server. Every call carries the session cookie and
 * nothing else; there is no token in localStorage to steal.
 */
class ApiError extends Error {
  constructor(status, message) {
    super(message);
    this.status = status;
  }
}

async function request(method, path, { body, form, signal } = {}) {
  const res = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers: body ? { 'content-type': 'application/json' } : undefined,
    body: form ?? (body ? JSON.stringify(body) : undefined),
    signal,
  });

  if (res.status === 204) return null;
  const text = await res.text();
  const data = text ? safeJson(text) : null;
  if (!res.ok) throw new ApiError(res.status, data?.error ?? `Request failed (${res.status})`);
  return data;
}

function safeJson(text) {
  try { return JSON.parse(text); } catch { return { raw: text }; }
}

const get = (p, opts) => request('GET', p, opts);
const post = (p, body) => request('POST', p, { body });
const patch = (p, body) => request('PATCH', p, { body });
const del = p => request('DELETE', p);
const upload = (p, form) => request('POST', p, { form });

export const api = {
  ApiError,

  signIn: (email, password) => post('/api/auth/sign-in/email', { email, password }),
  signOut: () => post('/api/auth/sign-out', {}),
  me: () => get('/api/me'),
  changePassword: (currentPassword, newPassword) => post('/api/me/password', { currentPassword, newPassword }),

  peekInvite: token => get(`/api/invite/peek?t=${encodeURIComponent(token)}`),
  setPassword: (token, password) => post('/api/invite/set-password', { token, password }),

  collections: () => get('/api/collections'),
  createCollection: body => post('/api/collections', body),
  updateCollection: (id, body) => patch(`/api/collections/${id}`, body),

  members: id => get(`/api/collections/${id}/members`),
  candidates: id => get(`/api/collections/${id}/candidates`),
  invite: (id, body) => post(`/api/collections/${id}/members`, body),
  setRole: (id, userId, role) => patch(`/api/collections/${id}/members/${userId}`, { role }),
  removeMember: (id, userId) => del(`/api/collections/${id}/members/${userId}`),
  resetPassword: (id, userId) => post(`/api/collections/${id}/members/${userId}/reset`, {}),

  addNote: (id, body) => post(`/api/collections/${id}/notes`, body),
  addFiles: (id, form) => upload(`/api/collections/${id}/files`, form),
  addFolder: (id, form) => upload(`/api/collections/${id}/folder`, form),
  addWebPage: (id, url) => post(`/api/collections/${id}/web`, { url, depth: 'page' }),
  documents: id => get(`/api/collections/${id}/documents`),
  removeDocument: docId => del(`/api/documents/${docId}`),
  document: docId => get(`/api/documents/${docId}`),

  jobs: () => get('/api/jobs'),
  retryJob: jobId => post(`/api/jobs/${jobId}/retry`, {}),

  search: (q, collection, signal) => get(
    `/api/search?q=${encodeURIComponent(q)}${collection ? `&collection=${collection}` : ''}`,
    { signal },
  ),

  shelf: (collection, kind) => get(
    `/api/shelf?${new URLSearchParams({
      ...(collection ? { collection } : {}),
      ...(kind && kind !== 'all' ? { kind } : {}),
    })}`,
  ),
  shelfUpload: (id, form) => upload(`/api/collections/${id}/shelf`, form),
  shelfUrl: itemId => get(`/api/shelf/${itemId}/url`),
  shelfSource: itemId => get(`/api/shelf/${itemId}/source`),
  shelfSkill: itemId => get(`/api/shelf/${itemId}/skill`),
  removeShelfItem: itemId => del(`/api/shelf/${itemId}`),
  updateShelfItem: (itemId, body) => patch(`/api/shelf/${itemId}`, body),
  replaceShelfFile: (itemId, form) => request('PUT', `/api/shelf/${itemId}/file`, { form }),
  replaceShelfHtml: (itemId, html) => request('PUT', `/api/shelf/${itemId}/file`, { body: { html } }),
  placeShelfItem: (itemId, collectionId) => post(`/api/shelf/${itemId}/collections`, { collectionId }),
  unplaceShelfItem: (itemId, collectionId) => del(`/api/shelf/${itemId}/collections/${collectionId}`),

  connect: () => get('/api/connect'),
  revokeClient: id => del(`/api/connect/clients/${id}`),
  createToken: name => post('/api/connect/tokens', { name }),
  revokeToken: prefix => del(`/api/connect/tokens/${prefix}`),

  status: () => get('/api/status'),
};
